// ─────────────────────────────────────────────────────────────────────────────
// stroke_classifier.ts — Time-aware primitives over StrokePoint[]
//
// Companion to `stroke_geometry.ts`. The geometry module ranks shape
// candidates by spatial fit (residual, straightness, axis alignment,
// …); this module classifies coarser intent using the timestamps every
// StrokePoint already carries from the canvas pipeline.
//
// MVP — single primitive: `isDwellPoint(points)`.
//
// A "dwell-point" is the gesture of holding the pen tip still on the
// page for long enough that the user clearly wants a single point
// inserted there. Distinct from a tap (too short, blocked by the tap-
// cadence cooldown) and from a real stroke (too long in displacement).
// The current heuristic recogniseShape() is unreliable here — strokes
// that are visually a "point" can land anywhere in the catalogue
// depending on accidental jitter — so we override with a pure time +
// displacement test.
//
// More primitives (line / curve / closed) follow in a later batch.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint } from '../shared/notebook_model';

export interface DwellOptions {
  /** Minimum duration (ms) the tip must be held for the stroke to
   *  count as a dwell-point. Default 280ms — comfortably past the
   *  tap window (340ms) so dwell and tap don't overlap when the tap
   *  envelope is exceeded; small enough that a deliberate hold feels
   *  responsive. */
  minDurationMs?: number;
  /** Maximum displacement (any-direction) the tip is allowed to
   *  drift over the whole stroke and still be classified as a
   *  dwell-point. Tight by design (12 px) — anything more is
   *  almost certainly the start of a real stroke. */
  maxDisplacementPx?: number;
}

const DEFAULT_DWELL: Required<DwellOptions> = {
  minDurationMs:    280,
  maxDisplacementPx: 12,
};

export interface DwellResult {
  /** Centre of the dwell — average of all sampled points. */
  x: number;
  y: number;
  /** Wall-clock duration of the dwell, ms. */
  durationMs: number;
  /** Total max-displacement-from-start in px (helpful for diagnostics). */
  maxDisplacementPx: number;
}

/** Classify a stroke as a dwell-point. Returns metadata when matched,
 *  null otherwise. The caller decides what to do with the result —
 *  typically replacing the freshly drawn stroke with a single point
 *  figure (or a vertex inserted onto an existing edge if the dwell
 *  landed on a snap-midpoint). */
export function classifyDwell(
  points: readonly StrokePoint[],
  opts: DwellOptions = {},
): DwellResult | null {
  if (points.length < 2) return null;
  const { minDurationMs, maxDisplacementPx } = { ...DEFAULT_DWELL, ...opts };

  const first = points[0];
  const last  = points[points.length - 1];
  const durationMs = last.timestamp - first.timestamp;
  if (durationMs < minDurationMs) return null;

  // Max displacement from the first point — guards against slow
  // drifts that would otherwise sneak through a simple first-vs-last
  // displacement test (a slow drag is NOT a dwell).
  let maxDisp = 0;
  let cx = 0, cy = 0;
  for (const p of points) {
    const dx = p.x - first.x;
    const dy = p.y - first.y;
    const d  = Math.sqrt(dx * dx + dy * dy);
    if (d > maxDisp) maxDisp = d;
    cx += p.x;
    cy += p.y;
  }
  if (maxDisp > maxDisplacementPx) return null;

  return {
    x: cx / points.length,
    y: cy / points.length,
    durationMs,
    maxDisplacementPx: maxDisp,
  };
}

/** Convenience boolean form. */
export function isDwellPoint(
  points: readonly StrokePoint[],
  opts: DwellOptions = {},
): boolean {
  return classifyDwell(points, opts) !== null;
}
